import { CallCountRequest, Event, EventList, MostLikelyJobRequest } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { type DeletedEventsFilterForm } from '../../modules/ComponentsLibrary/DeletedServiceCallsReport';
import {
  applyEventsFilter,
  EventClientService,
  loadEventsByFilterDeleted,
  ServicesRendered,
  type ServicesRenderedList,
} from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type EventFilters = EntityFilter<Event>;

export const useEventsQuery = ({
  enabled = true,
  filters,
}: {
  enabled?: boolean;
  filters: Parameters<typeof applyEventsFilter>['0'];
}) => {
  const requestWithAppliedFilters = useMemo(() => {
    const { req, ...rest } = filters;
    return applyEventsFilter({ req, ...rest });
  }, [filters]);

  return useQuery({
    queryKey: [
      queryKeys.events.root,
      queryKeys.events.list,
      queryKeys.events.getEventsListHash(requestWithAppliedFilters),
    ],
    queryFn: () => {
      return EventClientService.BatchGet(requestWithAppliedFilters);
    },
    enabled,
  });
};

const getEventQueryKeys = (filter: EventFilter) => [
  queryKeys.events.root,
  queryKeys.events.getEventHash(filter),
];

export const useEventQueryInline = () => {
  const queryClient = useQueryClient();

  const fetchEvent = useCallback(
    async ({ filter = {} }: { filter?: EventFilter } = {}) => {
      const queryKeys = getEventQueryKeys(filter);
      const cache = queryClient.getQueryData<Event>(queryKeys);
      if (!cache) {
        const userReq = Event.create(filter);
        const event = await EventClientService.Get(userReq);
        if (!event) throw new Error('Event not found');
        queryClient.setQueryData(queryKeys, event);
        return event;
      }
      return cache;
    },
    [queryClient],
  );

  return useMemo(() => ({ fetchEvent }), [fetchEvent]);
};

export type EventFilter = EntityFilter<Event>;
export const eventQuery = {
  getQueryKeys: (filter: EventFilter) => [
    queryKeys.events.root,
    queryKeys.events.getEventHash(filter),
  ],
  getQueryFn: (filter: EventFilter) => () => {
    const req = Event.create(filter);
    return EventClientService.Get(req);
  },
};

export const useEventQuery = <TSelectedData = Event,>({
  enabled = true,
  filter = {},
  select,
}: {
  enabled?: boolean;
  filter?: EventFilter;
  select?: (data: Awaited<ReturnType<typeof EventClientService.Get>>) => TSelectedData;
}) => {
  return useQuery({
    queryFn: eventQuery.getQueryFn(filter),
    queryKey: eventQuery.getQueryKeys(filter),
    enabled,
    select,
  });
};

export const useEventBatchGetQuery = <TSelectData = EventList,>({
  enabled = true,
  filters,
  select,
}: {
  enabled?: boolean;
  select?: (data: EventList | undefined) => TSelectData;
  filters: EventFilters;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.events.root,
      queryKeys.events.list,
      queryKeys.events.getEventsBatchGetHash(filters),
    ],
    queryFn: async () => {
      const req = Event.create(filters);
      return await EventClientService.BatchGet(req);
    },
    select,
    enabled,
  });
};

export const useDeletedEventsQuery = ({
  enabled = true,
  page,
  filter,
}: {
  enabled?: boolean;
  filter: DeletedEventsFilterForm;
  page: number;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.events.root,
      queryKeys.events.deletedEvents,
      queryKeys.events.getDeletedEventsHash({ filter, page }),
    ],
    queryFn: () => {
      return loadEventsByFilterDeleted({
        page: page,
        filter: filter,
        sort: {
          orderBy: 'date_started',
          orderByField: 'dateStarted',
          orderDir: 'ASC',
        },
        req: Event.create(),
      });
    },
    enabled,
  });
};

export const useEventCreateMutation = ({
  updateCalendarCache = false,
}: {
  updateCalendarCache?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: Event) => {
      return await EventClientService.Create(req);
    },
    async onSuccess(data, variables, context) {
      if (updateCalendarCache && data) {
        // Optimistically update the calendar cache
        updateCalendarCacheForEvent(queryClient, data, 'create');
      } else {
        // Fallback to invalidation
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
          queryClient.invalidateQueries({ queryKey: [queryKeys.serviceCalendar.root] }),
        ]);
      }
    },
  });
};

export const useEventDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: Event) => {
      return await EventClientService.Delete(req);
    },
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.serviceCalendar.root] }),
      ]);
    },
  });
};

export const useEventUpdateMutation = ({
  onSuccess: propOnSuccess,
  updateCalendarCache = false,
}: {
  onSuccess?: (data: Event | undefined, variables: Event, context: unknown) => unknown;
  updateCalendarCache?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  const onSuccess: (data: Event | undefined, variables: Event, context: unknown) => unknown = (
    data,
  ) => {
    if (data) {
      if (updateCalendarCache) {
        // Optimistically update the calendar cache
        updateCalendarCacheForEvent(queryClient, data, 'update');
      } else {
        // Fallback to existing logic
        queryClient.setQueriesData<Event | EventList>(
          { queryKey: [queryKeys.events.root] },
          (cache) => {
            if (cache) {
              if ('results' in cache) {
                if (cache.results.find((event) => event.id === data.id)) {
                  console.log({ cache: cache.results });
                  console.log({ dataId: data.id });

                  return EventList.create({
                    ...cache,
                    results: cache.results.map((event) => (event.id === data.id ? data : event)),
                  });
                }
              } else {
                if (cache.id === data.id) {
                  return data;
                }
              }
            }
          },
        );

        // Also invalidate service calendar queries to ensure calendar views stay in sync
        queryClient.invalidateQueries({ queryKey: [queryKeys.serviceCalendar.root] });
      }
    }
  };

  return useMutation({
    mutationFn: async (req: Event) => {
      return await EventClientService.Update(req);
    },
    onSuccess: propOnSuccess || onSuccess,
  });
};

// Utility function to update calendar cache for events
export const updateCalendarCacheForEvent = (
  queryClient: any,
  event: Event,
  method: 'create' | 'update' | 'delete',
) => {
  // Get all service calendar queries from the cache
  const serviceCalendarQueries = queryClient.getQueriesData({
    queryKey: [queryKeys.serviceCalendar.root],
  });

  serviceCalendarQueries.forEach(([queryKey, cachedData]: [any, any]) => {
    if (!cachedData?.dates) return;

    const eventDate = event.dateStarted?.split(' ')[0]; // Extract date part
    if (!eventDate) return;

    // Clone the cached data to avoid mutations
    const updatedCache = structuredClone(cachedData);

    // Find the date entry in the cache
    const dateEntry = updatedCache.dates[eventDate];
    if (!dateEntry) {
      // If date doesn't exist, create it
      if (method === 'create') {
        updatedCache.dates[eventDate] = {
          serviceCalls: [event],
          completedServiceCalls: [],
          reminders: [],
        };
      }
      return;
    }

    if (method === 'create') {
      // Add new event to serviceCalls
      if (!dateEntry.serviceCalls) dateEntry.serviceCalls = [];
      dateEntry.serviceCalls.push(event);
    } else if (method === 'update') {
      // Update existing event in both arrays
      if (dateEntry.serviceCalls) {
        const serviceCallIndex = dateEntry.serviceCalls.findIndex((e: Event) => e.id === event.id);
        if (serviceCallIndex !== -1) {
          dateEntry.serviceCalls[serviceCallIndex] = event;
        }
      }
      if (dateEntry.completedServiceCalls) {
        const completedIndex = dateEntry.completedServiceCalls.findIndex(
          (e: Event) => e.id === event.id,
        );
        if (completedIndex !== -1) {
          dateEntry.completedServiceCalls[completedIndex] = event;
        }
      }
    } else if (method === 'delete') {
      // Remove event from both arrays
      if (dateEntry.serviceCalls) {
        dateEntry.serviceCalls = dateEntry.serviceCalls.filter((e: Event) => e.id !== event.id);
      }
      if (dateEntry.completedServiceCalls) {
        dateEntry.completedServiceCalls = dateEntry.completedServiceCalls.filter(
          (e: Event) => e.id !== event.id,
        );
      }
    }

    // Update the cache
    queryClient.setQueryData(queryKey, updatedCache);
  });
};

export const useBatchEventUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: Event[]) => {
      return await Promise.allSettled(req.map((event) => EventClientService.Update(event)));
    },
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.serviceCalendar.root] }),
      ]);
    },
  });
};

export type MostLikelyJobRequestFilter = EntityFilter<MostLikelyJobRequest>;
export const useEventHintQuery = <TSelectData = EventList,>({
  filter,
  enabled,
  select,
}: {
  filter: MostLikelyJobRequestFilter;
  enabled?: boolean;
  select?: (data: EventList) => TSelectData;
}) => {
  return useQuery({
    queryKey: [queryKeys.jobHits.root, queryKeys.jobHits.hash(filter)],
    queryFn: async () => {
      return await EventClientService.BatchGetJobHints(MostLikelyJobRequest.create(filter));
    },
    enabled,
    select,
  });
};

export type CallCountRequestFilter = EntityFilter<CallCountRequest>;
export const getCallWindowQueryConfig = (filter: CallCountRequestFilter) => {
  return {
    queryKey: [
      queryKeys.events.root,
      queryKeys.events.callWindow,
      queryKeys.events.callWindow.getCallWindowHash(filter),
    ],
    queryFn: () => {
      return EventClientService.getCurrentDayServiceCallWindows(CallCountRequest.create(filter));
    },
  };
};
export const useCallWindowQuery = ({
  filter,
  enabled,
}: {
  filter: CallCountRequestFilter;
  enabled?: boolean;
}) => {
  return useQuery({ ...getCallWindowQueryConfig(filter), enabled });
};

/**
 * Hook to get the most recent services rendered for a list of event IDs
 */
export type ServicesRenderedFilter = {
  eventIds: number[];
};

export const useMostRecentServicesRenderedQuery = <TSelectData = ServicesRenderedList,>({
  filter,
  enabled = true,
  select,
}: {
  filter: ServicesRenderedFilter;
  enabled?: boolean;
  select?: (data: ServicesRenderedList | undefined) => TSelectData;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.events.root,
      'mostRecentServicesRendered',
      filter.eventIds.sort().join(','),
    ],
    queryFn: async () => {
      const req = ServicesRendered.create({
        idEventList: filter.eventIds.join(','),
      });
      return await EventClientService.GetMostRecentServicesRenderedByEventIDs(req);
    },
    enabled: enabled && filter.eventIds.length > 0,
    select,
  });
};
